import { assign, setup } from 'xstate';
import { toast } from 'sonner';

export type ToastTone = 'default' | 'success' | 'error';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
}

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
}

export type UiEvent =
  | { type: 'ui.overlay.open'; name: string }
  | { type: 'ui.overlay.close'; name: string }
  | { type: 'ui.overlay.closeAll' }
  | { type: 'ui.toast.push'; toast: ToastInput }
  | { type: 'ui.toast.dismiss'; id: string };

export interface UiContext {
  overlays: readonly string[];
  toasts: readonly Toast[];
}

export const UI_ACTOR_ID = 'ui';

export const uiMachine = setup({
  types: { context: {} as UiContext, events: {} as UiEvent },
  actions: {
    showToast: ({ event }) => {
      if (event.type !== 'ui.toast.push') return;
      const options = { description: event.toast.description };
      if (event.toast.tone === 'success') {
        toast.success(event.toast.title, options);
        return;
      }
      if (event.toast.tone === 'error') {
        toast.error(event.toast.title, options);
        return;
      }
      toast(event.toast.title, options);
    },
    dismissToast: ({ event }) => {
      if (event.type === 'ui.toast.dismiss') toast.dismiss(event.id);
    },
  },
}).createMachine({
  id: UI_ACTOR_ID,
  context: { overlays: [], toasts: [] },
  on: {
    'ui.overlay.open': {
      actions: assign({
        overlays: ({ context, event }) =>
          context.overlays.includes(event.name) ? context.overlays : [...context.overlays, event.name],
      }),
    },
    'ui.overlay.close': {
      actions: assign({
        overlays: ({ context, event }) => context.overlays.filter((name) => name !== event.name),
      }),
    },
    'ui.overlay.closeAll': {
      actions: assign({ overlays: () => [] }),
    },
    'ui.toast.push': {
      actions: 'showToast',
    },
    'ui.toast.dismiss': {
      actions: 'dismissToast',
    },
  },
});
